import React, { useEffect, useState } from 'react';
import Mail from '@mui/icons-material/Mail';
import { useTheme } from '@mui/material/styles';
import { Button, LinearProgress, TextField, Typography, useMediaQuery } from '@mui/material';
import { SelectStatus } from '../../Store/Selectors';
import { useAppSelector } from '../../Store/hooks';
import { SubscriptionsApi } from '../../Services';

const subscriptionsApi = new SubscriptionsApi();

interface Props {
  shipmentNumber: string;
}

export const StatusSubscription: React.FC<Props> = () => {
    const [email, setEmail] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    const currentStatus = useAppSelector(SelectStatus).currentStatus;

    const restrictedProductTypes = ['WA', 'ST', 'TRA'];
    const isSubscriptionDisabled = restrictedProductTypes.includes(currentStatus?.productTypeCode ?? '');
    
    // Check if shipment is in terminal status (same logic as backend and UI)
    // StatusCode 990201 should not allow subscriptions (Übergabe an PARTNER)
    // Handle both string and number types for statusCode by converting to string
    const statusCode = currentStatus?.statusCode;
    const isTerminalStatus = 
        currentStatus?.stage === 'Zugestellt' || 
        currentStatus?.stage === 'Unzustellbar' ||
        statusCode === 990201 ||
        String(statusCode) === '990201';

    useEffect(() => {
        // Proper email validation regex - validate trimmed email
        const trimmedEmail = email.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setIsValid(trimmedEmail.length > 0 && emailRegex.test(trimmedEmail));
    }, [email]);

    const subscribe = () => {
        if (!isValid) return;
        setIsSubscribing(true);
        setSubscriptionError('');

        subscriptionsApi.subscribe(email.trim(), currentStatus?.shipmentNumber ?? '').then((response) => {
            setIsSubscribed(response.status === 200);
            setIsSubscribing(false);
            if (response.status === 200) {
                setSubscriptionError('');
            } else {
                const errorMessage = response.error 
                    ? translateErrorMessage(response.error)
                    : getStatuErrorMessage(response.status);
                setSubscriptionError(errorMessage);
            }
        });
    };

    if (!currentStatus || isSubscriptionDisabled || isTerminalStatus) return null;

    return (
        <>
            <TextField
                label="Benachrichtigungen aktivieren"
                sx={{ width: matches ? '60%' : '100%', mt: 2 }}
                placeholder="E-Mail"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && isValid && subscribe()}
                disabled={isSubscribing || isSubscribed}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    endAdornment: (
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ margin: '1em 0' }}
                            disabled={!isValid || isSubscribing || isSubscribed}
                            onClick={subscribe}>
                            <Mail />
                        </Button>
                    )
                }}
            />
            {isSubscribing && <LinearProgress sx={{ width: matches ? '60%' : '100%' }} />}
            {isSubscribed && (
                <Typography color="primary" className="mt-2">
                    Wir senden Ihnen eine E-Mail, wenn sich der Status Ihrer Sendung ändert
                </Typography>
            )}
            {!!subscriptionError && (
                <Typography color="error" className="mt-2">
                    {subscriptionError}
                </Typography>
            )}
        </>
    );
};

const translateErrorMessage = (error: string): string => {
    const errorTranslations: Record<string, string> = {
        'Shipment is already in terminal status': 'Die Sendung befindet sich bereits in einem finalen Status (z.B. zugestellt oder unzustellbar). Benachrichtigungen können nicht mehr aktiviert werden.',
        'Invalid shipment number': 'Ungültige Sendungsnummer',
        'Email is not valid': 'Bitte überprüfen Sie, ob Ihre E-Mail-Adresse korrekt ist',
    };
    return errorTranslations[error] || error;
};

const getStatuErrorMessage = (status: number): string => {
    switch (status) {
        case 400:
            return 'Bitte überprüfen Sie, ob Ihre E-Mail-Adresse korrekt ist';
        case 404:
            return 'Sendung wurde nicht gefunden';
        case 409:
            return 'Sie haben diese Sendung bereits abonniert';
        default:
            return 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal';
    }
};
